<template>
  <div class="container py-5">

    <div class="columns hide-smallscreen flex--align-end">
      <div class="column">
        <label class="label">Search by Project</label>
        <search-autocomplete 
          :value="filters.project_id"
          @input="changeFilter('project_id', $event)"
          @updateProject="setProject($event)"/>
      </div>
      <div class="column">
        <label class="label">Search by Skid/ Group #</label>
        <v-search 
          placeholder="Search by Skid/ Group #"
          :search-input.sync="filters.skid_no"
          @search="changeFilter('skid_no', $event)"
          @clear="changeFilter('skid_no', null)"/>
      </div>
      <div class="column">
        <label class="label">Search by Piece #</label>
        <v-search 
          placeholder="Search by Piece #"
          :search-input.sync="filters.piece_no"
          @search="changeFilter('piece_no', $event)"
          @clear="changeFilter('piece_no', null)"/>
      </div>
      <div class="field px-3 pb-1">
        <label class="label">Sent Pieces Only</label>
        <v-switcher 
          :value="filters.is_sent"
          @input="$event == false ? 
            changeFilter('is_sent', null) :
            changeFilter('is_sent', true)"/>
      </div>
      <div class="field px-3 pb-1">
        <label class="label">Uninstalled Pieces Only</label>
        <v-switcher 
          :value="filters.no_status"
          @input="changeFilter('no_status', $event)"/>
      </div>
      <button
        @click="download" 
        class="button mb-3 mr-3">
        <span class="icon">
          <i class="fas fa-download"></i>
        </span>
      </button>
    </div>

    <div class="hide-largescreen px-3">

      <div class="flex flex--row flex--justify-space-between">
        <label class="label">
          Filters
        </label>

        <button
          @click="download" 
          class="button is-small">
          <span class="icon">
            <i class="fas fa-download"></i>
          </span>
        </button>
      </div>


      <div class="field is-grouped is-grouped-multiline">
        <div 
          v-for="(filterValue, filterName) in activeFilters" 
          :key="filterName"
          class="control">
          <div 
            class="tags has-addons">
            <span class="tag is-primary">
              <span class="pr-1">{{ filterNameMap[filterName] }}:</span>
              <span v-if="filterName != 'project_id'"> {{ filterValue }}</span>
              <span v-if="filterName == 'project_id' && project"> {{ project.project_name }}</span>
            </span>
            <a 
              @click="changeFilter(filterName, null)" 
              class="tag is-delete">
            </a>
          </div>
        </div>
      </div>

      <button
        @click="addingFilters = true" 
        class="button">
        Add Filters
      </button>
    </div>

    <hr/>

    <v-alert 
      class="my-3"
      v-if="!loading && installationItems.length == 0"
      type="info">
      <span v-if="numberOfFilters == 0">
        There are no items.
      </span>
      <span v-else>
        There are no items with the parameters used.
      </span> 
    </v-alert>

    <table v-if="installationItems.length > 0" 
      class="table is-fullwidth is-responsive">
      <thead>
        <tr>
          <th 
            v-for="(header, i) in headers"
            :key="i">
            {{ header }}
          </th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr 
          v-for="installationItem in installationItems"
          :key="installationItem.id">
          <td data-title="Project Name">
            {{ installationItem.installation_group.project.project_name }}
          </td>
          <td data-title="Skid/ Group #">
            {{ installationItem.installation_group.skid_no }}
          </td>
          <td data-title="Piece #">
            {{ installationItem.piece_no }}
          </td>
          <td data-title="Installation Order">
            {{ installationItem.install_order }}
          </td>
          <td data-title="Description">
            {{ installationItem.desc }}
          </td>
          <td class="hide-smallscreen">
            <div class="field has-addons">
              <p class="control">
                <button 
                  class="button"
                  :disabled="!installationItem.installation_group.is_sent || installationItem.status == 'Installed'"
                  :class="{ 'is-success': installationItem.status == 'Installed' }"
                  @click="updateInstallationItem({ 
                    installationItemId: installationItem.id, 
                    data: { status: 'Installed' }
                  })">
                  <span class="icon is-small">
                    <i class="fas fa-check"></i>
                  </span>
                  <span>Mark as Installed</span>
                </button>
              </p>
              <p class="control">
                <button 
                  class="button"
                  :class="{ 'is-danger': installationItem.status == 'Defected' }"
                  :disabled="!installationItem.installation_group.is_sent || installationItem.status == 'Defected'"
                  @click="launchDefectedDetailsModal(installationItem)">
                  <span class="icon is-small">
                    <i class="fas fa-times"></i>
                  </span>
                  <span>Mark as Defected</span>
                </button>
              </p>
            </div>
          </td>
          <td 
            :class="{ 'hide-smallscreen': !installationItem.status_note }"
            data-title="Status Notes">
            {{ installationItem.status_note }}
          </td>
          <td
            :class="{ 'hide-smallscreen': !installationItem.attachment }" 
            data-title="Image">
            <a 
              v-if="installationItem.attachment" 
              :href="installationItem.attachment"
              target="_blank">
              Image Attachment
            </a>
          </td>
          <td class="hide-largescreen">
            <div class="buttons">
              <button 
                class="button is-fullwidth"
                :disabled="!installationItem.installation_group.is_sent || installationItem.status == 'Installed'"
                :class="{ 'is-success': installationItem.status == 'Installed' }"
                @click="updateInstallationItem({ 
                  installationItemId: installationItem.id, 
                  data: { status: 'Installed' }
                })">
                <span class="icon is-small">
                  <i class="fas fa-check"></i>
                </span>
                <span>Mark as Installed</span>
              </button>
              <button 
                class="button is-fullwidth"
                :class="{ 'is-danger': installationItem.status == 'Defected' }"
                :disabled="!installationItem.installation_group.is_sent || installationItem.status == 'Defected'"
                @click="launchDefectedDetailsModal(installationItem)">
                <span class="icon is-small">
                  <i class="fas fa-times"></i>
                </span>
                <span>Mark as Defected</span>
              </button>
            </div>
          </td>
          <td :class="{ 'hide-smallscreen': installationItem.status != 'Defected' }" >
            <button 
              class="button"
              @click="launchDefectedDetailsModal(installationItem)"
              v-if="installationItem.status == 'Defected'">
              <span class="icon is-small">
                <i class="fas fa-edit"></i>
              </span>
            </button>
          </td>
        </tr>
      </tbody>
    </table>

    <v-confirm-modal 
      v-if="defectedDetailsModal.item"
      v-model="defectedDetailsModal.state"
      :title="defectedDetailsModal.title"
      :type="defectedDetailsModal.type"
      @confirm="defectedDetailsModal.confirmedCb()"
      @cancel="defectedDetailsModal.state = false"
      :confirmText="defectedDetailsModal.confirmText"
      >
      <div class="has-text-left mb-3">
        Please enter reason/ details for marking piece # 
        <strong>{{ defectedDetailsModal.item.piece_no }}</strong>
        as defected and add an image if applicable
      </div>
      <v-text-area 
        required
        placeholder="Enter defected item details"
        v-model="defectedDetailsModal.item.status_note">
      </v-text-area>
      <v-file-upload
        class="is-fullwidth mt-3 mb-5"
        has-name
        v-model="defectedDetailsModal.upload"/>
    </v-confirm-modal>

    <add-filters-modal v-model="addingFilters"/>

  </div>
</template>

<script>
import VSearch from '@/components/base/VSearch'
import VSwitcher from '@/components/base/VSwitcher'
import VConfirmModal from '@/components/base/VConfirmModal'
import VTextArea from '@/components/base/VTextArea'
import VFileUpload from '@/components/base/VFileUpload'
import VAlert from '@/components/base/VAlert'
import SearchAutocomplete from '@/components/projects/SearchAutocomplete'
import AddFiltersModal from '@/components/installationItems/AddFiltersModal'

import { downloadCsv } from '@/mixins/downloadable.js'

import { mapState, mapActions, mapMutations } from 'vuex'
import { format } from 'date-fns'

export default {
  components: { 
    VSearch, VSwitcher, SearchAutocomplete, VConfirmModal, VTextArea, 
    VFileUpload, VAlert, AddFiltersModal
  },
  mixins: [downloadCsv],
  data() {
    return {
      headers: [
        "Project", "Group/Skid #", "Piece #", "Installation Order", "Desc", 
        "Status", "Status Notes", "Image"
      ],
      defectedDetailsModal: {
        state: false,
        title: "Enter Details for Defected Part",
        confirmText: "Mark as Defected",
        type: "info",
        item: null,
        upload: { name: null, file: null, url: null }, 
        confirmedCb: v => v
      },
      filterNameMap: {
        "project_id": "Project", 
        "skid_no": "Skid/ Group #",
        "piece_no": "Piece #",
        "is_sent": "Sent Only",
        "no_status": "Uninstalled Only"
      },
      addingFilters: false,
      formatCsvDate: (d) => {
        return d ? format(d, 'yyyy-MM-dd_H.mm.ss') : null
      }
    } 
  },
  computed: {
    ...mapState("installationItems", [
      "installationItems", "loading", "selected", "filters", "pagination",
      "project"
    ]),
    numberOfFilters({ filters }) {
      return Object.values(filters).filter(d => d != null && d != false).length
    },
    activeFilters({ filters }) {
      return Object.fromEntries(
        Object.entries(filters).filter(d => d[1] != null && d[1] != false)
      )
    }
  },
  watch: {
    "pagination.page"(newPage, oldPage) {
      let params = {...this.pagination, ...this.filters}
      this.getInstallationItems(params);
    },
    '$route.query': {
      handler(query) {
        this.getInstallationItems(query);
      },
      deep: true
    }
  },  
  created() {
    this.initialize();
  },
  methods: {
    ...mapActions("installationItems", [
      "getInstallationItems", "updateInstallationItem", "addAttachment"
    ]),
    ...mapMutations("installationItems", ["setFilter", "setProject"]),
    ...mapMutations("app", ["setNotification"]),
    initialize() {
      let query = this.$route.query;
      if (query) {
        Object.keys(query).forEach(key => {
          this.setFilter({ field: key, value: query[key] })
        })
        this.getInstallationItems(query);
      } else {
        this.getInstallationItems();
      }
    },
    changeFilter(field, value) {
      this.setFilter({ field: field, value: value });
      let query = Object.assign({}, this.$route.query);
      if (value) {  
        query[field] = value;
        this.$router.push({ name: 'InstallationItems', query: query });
      } else {
        delete query[field];
        this.$router.push({ name: 'InstallationItems', query: query });
      }
    },
    launchDefectedDetailsModal(installationItem) {
      const { defectedDetailsModal } = this;

      defectedDetailsModal.item = installationItem;
      defectedDetailsModal.state = true;
      defectedDetailsModal.confirmedCb = () => { 
        if (defectedDetailsModal.item.status_note) {
          this.saveDefectedDetails();
          defectedDetailsModal.state = false;
        } else {
          this.setNotification({
            type: 'danger',
            status: true,
            message: `Please add details for marking piece ${defectedDetailsModal.item.piece_no} as defected.`
          });
        }
      }
    },
    download() {
      this.downloadCsv({ 
        data: this.installationItems.map(d => {
          let res = {}
          res['Project Name'] = d.installation_group.project.project_name;
          res['Skid/ Group #'] = d.installation_group.skid_no;
          let otherFields = [
            { field: 'piece_no', label: 'Piece #' },  
            { field: 'install_order', label: 'Installation Order' },
            { field: 'desc', label: 'Description' },
            { field: 'status', label: 'Status' },
            { field: 'status_note', label: 'Status Note' },
            { field: 'attachment', label: 'Image Attachment Link' }
          ]
          
          otherFields.forEach(o => {
            res[o.label] = d[o.field]
          })

          return res
        }), 
        filename: `installation_items_${this.formatCsvDate(new Date)}.csv` 
      });
    },
    saveDefectedDetails() {
      const { defectedDetailsModal } = this;

      this.updateInstallationItem({ 
        installationItemId: defectedDetailsModal.item.id,
        data: {
          status: "Defected",
          status_note: defectedDetailsModal.item.status_note
        }
      }).then(
        () => {
          if (defectedDetailsModal.upload.file) {
            let formData = new FormData();
            formData.append('attachment', defectedDetailsModal.upload.file, defectedDetailsModal.upload.name);
            this.addAttachment({ 
              installationItemId: defectedDetailsModal.item.id,
              data: formData
            }).then(() => {
              this.clearDefectedDetailsModal();
            })
          } else {
            this.clearDefectedDetailsModal();
          }
        }
      )
    },
    clearDefectedDetailsModal() {
      this.defectedDetailsModal.item = null;
      this.defectedDetailsModal.upload = { name: null, file: null, url: null };
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/styles/table.scss';
@import '@/styles/flexbox.scss';
@import '@/styles/responsive.scss';
</style>